import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Loading } from 'savings-js';
import './IncentivePrograms.css';
import { API_URL } from '../const'

// Types
interface Partner {
  id: number;
  name: string;
  description: string;
  address: string;
}

interface Trigger {
  id: number;
  name: string;
  ruleset: any;
}

interface Precondition {
  id: number;
  name: string;
  listOfConditions: any;
}

interface Program {
  id: number;
  name: string;
  valuePaid?: number;
  value_paid?: number; // For database field name compatibility
  dateStart?: string;
  date_start?: string; // For database field name compatibility
  dateEnd?: string | null;
  date_end?: string | null; // For database field name compatibility
  partnerId?: number;
  partner_id?: number; // For database field name compatibility
  triggerId?: number;
  trigger_id?: number; // For database field name compatibility
  preconditionId?: number;
  precondition_id?: number; // For database field name compatibility
  Partner?: Partner;
  Trigger?: Trigger;
  Precondition?: Precondition;
}

const IncentivePrograms: React.FC = () => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [preconditions, setPreconditions] = useState<Precondition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Check if we're returning from a form submission
    if (location.state && location.state.success) {
      setSuccessMessage(location.state.message || 'Operation completed successfully!');
      
      // Clear the success message after 3 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [location]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [programsRes, partnersRes, triggersRes, preconditionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/programs`),
        axios.get(`${API_URL}/api/partners`),
        axios.get(`${API_URL}/api/triggers`),
        axios.get(`${API_URL}/api/preconditions`),
      ]);
      
      // Normalize program data to handle different field naming conventions
      const normalizedPrograms = programsRes.data.map((program: Program) => ({
        id: program.id,
        name: program.name,
        valuePaid: program.valuePaid || program.value_paid,
        dateStart: program.dateStart || program.date_start,
        dateEnd: program.dateEnd || program.date_end,
        partnerId: program.partnerId || program.partner_id,
        triggerId: program.triggerId || program.trigger_id,
        preconditionId: program.preconditionId || program.precondition_id,
        Partner: program.Partner,
        Trigger: program.Trigger,
        Precondition: program.Precondition
      }));
      
      setPrograms(normalizedPrograms);
      setPartners(partnersRes.data);
      setTriggers(triggersRes.data);
      setPreconditions(preconditionsRes.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const handleCreateProgram = () => {
    navigate('/programs/create');
  };
  
  const handleEditProgram = (id: number) => {
    navigate(`/programs/edit/${id}`);
  };
  
  const handleViewProgramDetails = (id: number) => {
    navigate(`/programs/details/${id}`);
  };
  
  const handleDeleteProgram = async (id: number, name: string) => {
    if (window.confirm(`Are you sure you want to delete this program? This action cannot be undone.`)) {
      try {
        setLoading(true);
        await axios.delete(`/api/programs/${id}`);
        
        // Refresh the programs list
        fetchData();
        
        setSuccessMessage(`Program "${name}" deleted successfully!`);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } catch (err: any) {
        console.error('Error deleting program:', err);
        setError('Failed to delete program. Please try again later.');
        setLoading(false);
      }
    }
  };
  
  // Helper functions for program status
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isActiveProgram = (program: Program) => {
    const now = new Date();
    const startDate = program.dateStart || program.date_start ? new Date(program.dateStart || program.date_start || '') : null;
    const endDate = program.dateEnd || program.date_end ? new Date(program.dateEnd || program.date_end || '') : null;
    
    return (startDate ? startDate <= now : true) && (endDate ? endDate >= now : true);
  };
  
  const isPastProgram = (program: Program) => {
    const now = new Date();
    const endDate = program.dateEnd || program.date_end ? new Date(program.dateEnd || program.date_end || '') : null;
    
    return endDate ? endDate < now : false;
  };
  
  const isFutureProgram = (program: Program) => {
    const now = new Date();
    const startDate = program.dateStart || program.date_start ? new Date(program.dateStart || program.date_start || '') : null;
    
    return startDate ? startDate > now : false;
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-box">
          <h1 className="loading-title">Loading Programs...</h1>
          <div className="loading-spinner-container">
            <Loading />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="incentive-programs-container">
      <div className="header">
        <h1>Incentive Programs</h1>
        <div className="header-actions">
          <Button 
            onClick={handleCreateProgram}
            label="Create New Program"
            color="primary"
          />
        </div>
      </div>
      
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
          <Button 
            onClick={() => fetchData()}
            label="Retry"
            color="primary"
            className="mt-2"
          />
        </div>
      )}
      
      {loading ? (
        <div className="loading-spinner">
          <Loading />
        </div>
      ) : programs.length > 0 ? (
        <div className="programs-grid">
          {programs.map(program => {
            const programStatus = isPastProgram(program) ? 'expired' : 
                                 isFutureProgram(program) ? 'upcoming' : 
                                 'active';
            
            return (
              <div key={program.id} className={`program-card program-status-${programStatus}`}>
                <div className="program-header">
                  <h2 className="program-name">{program.name}</h2>
                  <div className="card-actions">
                    <button
                      className="action-button view-button"
                      onClick={() => handleViewProgramDetails(program.id)}
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    <button
                      className="action-button edit-button"
                      onClick={() => handleEditProgram(program.id)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleDeleteProgram(program.id, program.name)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                <div className="program-details">
                  <div className="program-status-row">
                    <div className="program-value">${program.valuePaid || program.value_paid}</div>
                    <div className={`program-status ${programStatus}`}>
                      {programStatus === 'active' ? 'Active' : 
                       programStatus === 'expired' ? 'Expired' : 'Upcoming'}
                    </div>
                  </div>
                  <div className="program-dates">
                    <div className="date-range">
                      <span className="date-label">From:</span> {formatDate(program.dateStart || program.date_start)}
                    </div>
                    <div className="date-range">
                      <span className="date-label">To:</span> {formatDate(program.dateEnd || program.date_end)}
                    </div>
                  </div>
                  <div className="program-tags">
                    {program.Partner && (
                      <div className="tag partner-tag">
                        <span className="tag-icon"><i className="fas fa-handshake"></i></span>
                        <span className="tag-text">{program.Partner.name}</span>
                      </div>
                    )}
                    {program.Trigger && (
                      <div className="tag trigger-tag">
                        <span className="tag-icon"><i className="fas fa-bolt"></i></span>
                        <span className="tag-text">{program.Trigger.name}</span>
                      </div>
                    )}
                    {program.Precondition && (
                      <div className="tag precondition-tag">
                        <span className="tag-icon"><i className="fas fa-check-circle"></i></span>
                        <span className="tag-text">{program.Precondition.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="no-programs">
          <p>No incentive programs found. Create your first program!</p>
          <Button 
            onClick={handleCreateProgram}
            label="Create Program"
            color="primary"
            className="mt-4"
          />
        </div>
      )}
    </div>
  );
};

export default IncentivePrograms; 